import {
  Check,
  Download,
  ExternalLink,
  FileText,
  RefreshCw,
  Send,
  Trash2,
} from "lucide-react";
import { useState } from "react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router";
import {
  downloadPrintOrdersCsv,
  useAdminPrinterApiStatus,
  useAdminPrintOrders,
  useConfirmPrintOrder,
  useDeletePrintOrder,
  useRefreshPrintOrderTracking,
  useSendPrintOrderToPrinter,
} from "../../api/admin";
import { AdminPrintOrder } from "../../types/admin";
import { formatDate } from "../../utils/dateUtils";
import { DEFAULT_PAGE } from "./pagination";
import { apiErrorMessage, notify } from "./notify";
import { Badge, Button, Card, PageHeader, PaginationBar } from "./ui";

const TABS: [string, string][] = [
  ["pending", "À traiter"],
  ["confirmed", "Confirmées"],
  ["sent", "Envoyées à l'imprimeur"],
  ["printer_error", "Erreur imprimeur"],
  ["", "Toutes"],
];

// Printer's API (Les Bonnes Feuilles): is the key set and valid?
const PrinterApiBanner = () => {
  const { data } = useAdminPrinterApiStatus();
  if (!data) return null;

  const [className, text] = !data.configured
    ? [
        "bg-yellow-50 text-yellow-800 border-yellow-200",
        "API de l'imprimeur non configurée (LBF_API_KEY) : les commandes confirmées partent par email.",
      ]
    : data.error
      ? [
          "bg-red-50 text-red-700 border-red-200",
          `API de l'imprimeur injoignable : ${data.error}`,
        ]
      : [
          "bg-green-50 text-green-800 border-green-200",
          `API de l'imprimeur connectée (${data.partner?.name}) : les commandes confirmées sont envoyées automatiquement, le suivi est mis à jour toutes les 2 h.`,
        ];

  return (
    <div className={`mb-4 px-4 py-2.5 text-sm rounded-md border ${className}`}>
      {text}
    </div>
  );
};

const PrinterTracking = ({ order }: { order: AdminPrintOrder }) => {
  if (order.sent_to_printer_at) {
    return (
      <div className="mt-2 space-y-1">
        <Badge color="blue">Envoyée à l'imprimeur</Badge>
        <p className="text-xs text-gray-400">
          {formatDate(order.sent_to_printer_at)} · {order.printer_reference}
        </p>
        {order.printer_status && (
          <p className="text-xs text-gray-700">
            {order.printer_status}
            {order.printer_carrier ? ` · ${order.printer_carrier}` : ""}
          </p>
        )}
        {order.printer_tracking_url && (
          <a
            href={order.printer_tracking_url}
            target="_blank"
            rel="noreferrer"
            className="flex items-center gap-1 text-xs text-indigo-600 hover:underline"
          >
            Suivi du colis <ExternalLink className="w-3 h-3" />
          </a>
        )}
      </div>
    );
  }
  if (order.printer_error) {
    return (
      <div className="mt-2 space-y-1 max-w-[220px]">
        <Badge color="red">Erreur imprimeur</Badge>
        <p className="text-xs text-red-600 break-words">
          {order.printer_error}
        </p>
      </div>
    );
  }
  return null;
};

export const AdminPrintOrders = () => {
  const [status, setStatus] = useState("pending");
  const [page, setPage] = useState(DEFAULT_PAGE);
  const { data, isLoading } = useAdminPrintOrders(status || undefined, page);
  const confirm = useConfirmPrintOrder();
  const remove = useDeletePrintOrder();
  const sendToPrinter = useSendPrintOrderToPrinter();
  const refreshTracking = useRefreshPrintOrderTracking();
  const navigate = useNavigate();

  return (
    <>
      <PageHeader
        title="Commandes d'impression"
        subtitle={data ? `${data.pagination.total} commande(s)` : undefined}
        action={
          <Button
            onClick={() =>
              downloadPrintOrdersCsv(status || undefined).catch((error) =>
                toast.error(apiErrorMessage(error, "Export impossible")),
              )
            }
          >
            <Download className="w-4 h-4" /> Export CSV
          </Button>
        }
      />

      <PrinterApiBanner />

      <div className="flex flex-wrap gap-1 mb-4">
        {TABS.map(([value, label]) => (
          <button
            key={value}
            onClick={() => {
              setStatus(value);
              setPage((p) => ({ ...p, page: 1 }));
            }}
            className={`px-4 py-1.5 text-sm font-medium rounded-md ${
              status === value
                ? "bg-indigo-600 text-white"
                : "text-gray-600 hover:bg-gray-100"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      <Card>
        {isLoading ? (
          <div className="p-6 text-center text-gray-500">Chargement…</div>
        ) : data && data.print_orders.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500 uppercase border-b border-gray-100">
                <th className="px-5 py-3 font-medium">Destinataire</th>
                <th className="px-5 py-3 font-medium">Adresse</th>
                <th className="px-5 py-3 font-medium">Qté</th>
                <th className="px-5 py-3 font-medium">Statut</th>
                <th className="px-5 py-3 font-medium">PDF</th>
                <th className="px-5 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {data.print_orders.map((order) => (
                <tr key={order.id} className="hover:bg-gray-50 align-top">
                  <td className="px-5 py-3">
                    <p className="font-medium text-gray-900">
                      {order.first_name} {order.last_name}
                    </p>
                    <button
                      onClick={() => navigate(`/admin/users/${order.user_id}`)}
                      className="text-xs text-indigo-600 hover:underline"
                    >
                      {order.user_email}
                    </button>
                    <p className="text-xs text-gray-400">{order.phone}</p>
                  </td>
                  <td className="px-5 py-3 text-gray-600 text-xs whitespace-pre-line max-w-[220px]">
                    {order.address}
                    {order.address_line_2 ? `, ${order.address_line_2}` : ""}
                    {"\n"}
                    {order.zip_code} {order.city}, {order.country}
                  </td>
                  <td className="px-5 py-3 font-semibold text-gray-900">
                    {order.quantity ?? 0}
                  </td>
                  <td className="px-5 py-3">
                    {order.is_confirmed ? (
                      <div>
                        <Badge color="green">Confirmée</Badge>
                        <p className="text-xs text-gray-400 mt-1">
                          {formatDate(order.confirmed_at)}
                        </p>
                      </div>
                    ) : (
                      <Badge color="yellow">En attente</Badge>
                    )}
                    <PrinterTracking order={order} />
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col gap-1">
                      {order.book_pdf_url && (
                        <a
                          href={order.book_pdf_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                        >
                          <FileText className="w-3.5 h-3.5" /> Contenu
                          <Download className="w-3 h-3" />
                        </a>
                      )}
                      {order.cover_pdf_url && (
                        <a
                          href={order.cover_pdf_url}
                          target="_blank"
                          rel="noreferrer"
                          className="flex items-center gap-1 text-xs text-indigo-600 hover:underline"
                        >
                          <FileText className="w-3.5 h-3.5" /> Couverture
                          <Download className="w-3 h-3" />
                        </a>
                      )}
                      {!order.book_pdf_url && !order.cover_pdf_url && (
                        <span className="text-xs text-gray-400">—</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <div className="flex flex-col items-end gap-2">
                      {!order.is_confirmed && (
                        <button
                          onClick={() =>
                            confirm.mutate(order.id, {
                              onSuccess: () => {
                                toast.success("Commande confirmée");
                              },
                            })
                          }
                          disabled={confirm.isLoading}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white text-xs font-medium rounded-md hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="w-3.5 h-3.5" /> Confirmer
                        </button>
                      )}
                      {order.is_confirmed && !order.sent_to_printer_at && (
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() =>
                            sendToPrinter.mutate(
                              order.id,
                              notify("Commande envoyée à l'imprimeur"),
                            )
                          }
                          disabled={sendToPrinter.isLoading}
                          confirm={`Envoyer la commande de ${order.first_name} ${order.last_name} (${order.quantity ?? 0} ex.) à l'imprimeur ? Elle ne pourra plus être modifiée chez lui.`}
                        >
                          <Send className="w-3.5 h-3.5" />
                          {order.printer_error ? "Renvoyer" : "Envoyer"}
                        </Button>
                      )}
                      {order.sent_to_printer_at && (
                        <Button
                          size="sm"
                          onClick={() =>
                            refreshTracking.mutate(
                              order.id,
                              notify("Suivi mis à jour"),
                            )
                          }
                          disabled={refreshTracking.isLoading}
                        >
                          <RefreshCw className="w-3.5 h-3.5" /> Suivi
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="danger"
                        onClick={() =>
                          remove.mutate(order.id, notify("Commande supprimée"))
                        }
                        disabled={remove.isLoading}
                        confirm={`Supprimer la commande de ${order.first_name} ${order.last_name} ? Les compteurs de l'abonnement ne sont pas modifiés.`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="p-6 text-center text-gray-400">Aucune commande</div>
        )}
        <PaginationBar pagination={data?.pagination} onChange={setPage} />
      </Card>
    </>
  );
};
